import React, { useState, useEffect } from 'react';
import * as QRCode from 'qrcode';
import './invoice-print.css';
import {
  UserIcon,
  TruckIcon,
  UsersIcon,
  ClipboardDocumentListIcon,
  BanknotesIcon,
  GlobeAltIcon,
  IdentificationIcon,
  DevicePhoneMobileIcon,
  PhoneIcon,
  MapPinIcon,
  QrCodeIcon,
} from '@heroicons/react/24/outline';
import { Booking } from '../../types';
import { DateDisplay } from '../Forms';
import apiService from '../../services/api';
import { getApiUrl } from '../../config/environment';
import BoardingQrCode from '../Boarding/BoardingQrCode';

// ZATCA QR Code Generator Utility (Browser-compatible)
const generateZATCAQRCode = (
  sellerName: string,
  vatNumber: string,
  invoiceDate: string,
  invoiceTotal: number,
  vatAmount: number
): string => {
  // ZATCA TLV Tags
  const TAGS = {
    SELLER_NAME: 0x01,
    VAT_NUMBER: 0x02,
    INVOICE_DATE: 0x03,
    INVOICE_TOTAL: 0x04,
    VAT_AMOUNT: 0x05
  };

  // Helper function to create TLV data (browser-compatible)
  const createTLV = (tag: number, value: string): Uint8Array => {
    const valueBytes = new TextEncoder().encode(value);
    const length = valueBytes.length;

    // ZATCA Phase 1 TLV: [tag (1 byte)][length (1 byte)][value...]
    const tlv = new Uint8Array(2 + length);
    tlv[0] = tag;
    tlv[1] = Math.min(length, 0xFF);
    tlv.set(valueBytes.slice(0, 0xFF), 2);

    return tlv;
  };

  // Create TLV data for each field
  const tlvs = [
    createTLV(TAGS.SELLER_NAME, sellerName),
    createTLV(TAGS.VAT_NUMBER, vatNumber),
    createTLV(TAGS.INVOICE_DATE, invoiceDate),
    createTLV(TAGS.INVOICE_TOTAL, invoiceTotal.toFixed(2)),
    createTLV(TAGS.VAT_AMOUNT, vatAmount.toFixed(2))
  ];

  // Combine all TLVs
  const totalLength = tlvs.reduce((sum, tlv) => sum + tlv.length, 0);
  const combined = new Uint8Array(totalLength);
  let offset = 0;

  for (const tlv of tlvs) {
    combined.set(tlv, offset);
    offset += tlv.length;
  }

  // Convert to Base64 (proper binary string handling)
  let binaryString = '';
  for (let i = 0; i < combined.length; i++) {
    binaryString += String.fromCharCode(combined[i]);
  }
  const base64 = btoa(binaryString);
  return base64;
};

interface InvoiceProps {
  booking: Booking;
  /** When provided (public share page), skip authenticated vendor profile fetch */
  vendorInfo?: VendorInfo;
}

interface VendorLicense {
  commercialRegister?: string;
  tourismLicense?: string;
  transportationLicense?: string;
  taxNumber?: string;
}

interface VendorInfo {
  name?: string;
  companyName?: string;
  license?: VendorLicense;
  contact?: {
    address?: {
      street?: string;
      city?: string;
      state?: string;
      country?: string;
    };
    phone?: string;
    mobile?: string;
    email?: string;
  };
  logo?: string;
}

const INVOICE_HEADER_BG = '/headerHAJbg.png';
const INVOICE_COMPANY_LOGO = '/logo3.png';
const INVOICE_CONTACT_PHONES = ['0506228545', '0506079645'];

const getLogoUrl = (logoPath: string | null | undefined) => {
  if (!logoPath) return null;
  if (logoPath.startsWith('http')) return logoPath;
  const base = getApiUrl().replace(/\/api\/?$/, '');
  return `${base}${logoPath.startsWith('/') ? logoPath : `/${logoPath}`}`;
};

/** عناوين التراخيص — ذهبي بني */
const INVOICE_LICENSE_LABEL_CLASS = 'invoice-license-label text-sm sm:text-lg font-bold text-[#a0753d]';
/** قيم التراخيص — غيّر اللون هنا (مثلاً text-white) */
const INVOICE_LICENSE_VALUE_CLASS = 'invoice-license-value text-xs sm:text-sm text-white';

const INVOICE_SECTION_CLASS = 'mb-4';
const INVOICE_SECTION_TITLE_CLASS = 'font-bold text-lg text-gray-900';
const INVOICE_SECTION_TITLE_ROW_CLASS = 'flex items-center justify-start gap-2 mb-2';

const InvoiceTitleIcon: React.FC<{ icon: React.ComponentType<React.SVGProps<SVGSVGElement>> }> = ({ icon: Icon }) => (
  <span className="invoice-title-icon-box inline-flex items-center justify-center shrink-0">
    <Icon className="invoice-title-icon w-6 h-6" aria-hidden />
  </span>
);

/** يقسّم اسم المكتب: مكتب (ذهبي) | الاسم (أسود) | للنقل البري (ذهبي) */
function parseOfficeCompanyName(name: string): { middle: string } | null {
  const match = name.trim().match(/^مكتب\s+(.+)\s+(للنقل البري)$/);
  if (!match) return null;
  return { middle: match[1].trim() };
}

const InvoiceCompanyName: React.FC<{ name: string }> = ({ name }) => {
  const parsed = parseOfficeCompanyName(name);
  if (!parsed) {
    return <span className="text-gray-900">{name}</span>;
  }

  return (
    <div className="invoice-company-title flex flex-col items-center leading-tight gap-0.5">
      <div className="invoice-company-office-row inline-flex items-center justify-center gap-2.5 sm:gap-3">
        <span className="invoice-company-line" aria-hidden />
        <span className="invoice-company-gold text-base sm:text-3xl">مكتب</span>
        <span className="invoice-company-line" aria-hidden />
      </div>
      <span className="invoice-company-name">{parsed.middle}</span>
      <span className="invoice-company-gold text-base sm:text-3xl">للنقل البري</span>
    </div>
  );
};

const INVOICE_SECTION_FRAME_CLASS = 'invoice-section-frame rounded-xl p-4 sm:p-5 bg-transparent';
const INVOICE_INNER_DIVIDER_CLASS = 'invoice-inner-divider border-t border-gray-200 my-3';

const InvoiceStackedField: React.FC<{
  label: string;
  children: React.ReactNode;
  separated?: boolean;
  icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
}> = ({ label, children, separated = false, icon: Icon }) => (
  <div
    className={`invoice-stacked-field flex flex-1 flex-col items-center justify-center text-center px-3 py-2 min-w-0 ${
      separated ? 'invoice-stacked-field-sep' : ''
    }`}
  >
    <span className="invoice-field-label font-bold text-base text-black mb-1 inline-flex items-center justify-center gap-1">
      {Icon && <Icon className="invoice-field-icon w-4 h-4 shrink-0" aria-hidden />}
      <span>{label}:</span>
    </span>
    <span className="text-sm text-gray-800 break-words">{children}</span>
  </div>
);

const InvoiceSection: React.FC<{
  title: string;
  children: React.ReactNode;
  className?: string;
  icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  framed?: boolean;
}> = ({ title, children, className = '', icon: Icon, framed = true }) => (
    <div className={`invoice-section-block ${INVOICE_SECTION_CLASS} ${className}`.trim()}>
    <div className={`invoice-section-title-row ${INVOICE_SECTION_TITLE_ROW_CLASS}`}>
      {Icon && <InvoiceTitleIcon icon={Icon} />}
      <h3 className={`invoice-section-title ${INVOICE_SECTION_TITLE_CLASS}`}>{title}</h3>
    </div>
    {framed ? (
      <div className={INVOICE_SECTION_FRAME_CLASS}>{children}</div>
    ) : (
      children
    )}
  </div>
);

const InvoiceCurrency: React.FC<{ amount: number; prefix?: string; className?: string }> = ({
  amount,
  prefix,
  className = '',
}) => {
  const formatted = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);

  return (
    <span className={`invoice-currency inline-flex items-baseline gap-1 ${className}`.trim()}>
      {prefix ? <span>{prefix}</span> : null}
      <span>{formatted}</span>
      <span>ر.س</span>
    </span>
  );
};

const InvoiceAmountRow: React.FC<{ label: string; children: React.ReactNode; highlight?: boolean }> = ({
  label,
  children,
  highlight = false,
}) => (
  <div className={`invoice-amount-row flex justify-between items-center py-2.5 ${highlight ? 'text-orange-600' : ''}`}>
    <span className={`font-bold shrink-0 text-base ${highlight ? 'text-orange-600' : 'text-black'}`}>{label}</span>
    <span className={`font-bold text-lg ${highlight ? 'text-orange-600' : 'text-black'}`}>{children}</span>
  </div>
);

const InvoicePassengerCounts: React.FC<{ maleCount: number; femaleCount: number }> = ({
  maleCount,
  femaleCount,
}) => (
  <>
    <div className={INVOICE_INNER_DIVIDER_CLASS} />
    <div className="invoice-passenger-counts flex flex-wrap items-center gap-x-6 gap-y-1 text-base">
      <span className="inline-flex items-center gap-2">
        <span className="text-gray-500">عدد الذكور</span>
        <span className="font-semibold text-gray-900">{maleCount}</span>
      </span>
      <span className="text-gray-300 select-none" aria-hidden>|</span>
      <span className="inline-flex items-center gap-2">
        <span className="text-gray-500">عدد الإناث</span>
        <span className="font-semibold text-gray-900">{femaleCount}</span>
      </span>
    </div>
  </>
);

const InvoiceRouteArrow: React.FC = () => (
  <svg
    className="invoice-route-arrow w-8 max-w-[2rem] h-[8px] shrink-0"
    viewBox="0 0 80 10"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <path
      d="M0 5L10 0.5V3.5H78V6.5H10V9.5L0 5Z"
      fill="#c9a227"
    />
  </svg>
);

const InvoiceRouteBlock: React.FC<{
  index: number;
  from: string;
  to: string;
  dateValue: string;
  time?: string;
}> = ({ index, from, to, dateValue, time }) => (
  <div className="invoice-route-block invoice-keep-together rounded-lg border border-[#e8dcc0] bg-transparent p-3 sm:p-4">
    <div className="invoice-route-row">
      <span className="invoice-route-index inline-flex w-7 h-7 shrink-0 items-center justify-center rounded-full bg-[#c9a227] text-white font-bold text-sm justify-self-center">
        <span className="invoice-route-index-num">{index}</span>
      </span>
      <span className="font-bold text-black whitespace-nowrap">من:</span>
      <span className="invoice-route-city font-bold text-gray-900" title={from}>{from}</span>
      <InvoiceRouteArrow />
      <span className="font-bold text-black whitespace-nowrap invoice-route-to-label">إلى:</span>
      <span className="invoice-route-city font-bold text-gray-900" title={to}>{to}</span>

      <div className="invoice-route-meta flex flex-nowrap items-center justify-start gap-x-2 gap-y-0 min-w-0">
        <span className="font-bold text-black text-sm whitespace-nowrap">المغادرة:</span>
        <DateDisplay
          value={dateValue}
          dualLayout="inline"
          dualSeparator="|"
          className="inline-flex items-center gap-x-1.5"
          gregorianClassName="text-sm font-semibold text-gray-900 leading-none whitespace-nowrap"
          hijriClassName="text-sm font-semibold text-gray-900 leading-none whitespace-nowrap"
        />
        {time ? (
          <span className="text-base font-semibold text-gray-900 whitespace-nowrap leading-none">{time}</span>
        ) : null}
      </div>
    </div>
  </div>
);

type VendorAddress = NonNullable<VendorInfo['contact']>['address'];

const buildOfficeMapsUrl = (address?: VendorAddress): string | null => {
  if (!address) return null;
  const parts = [address.street, address.city, address.state, address.country].filter(Boolean);
  if (parts.length === 0) return null;
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(parts.join(', '))}`;
};


const formatPhonesInPairs = (phones: string[]): string[] => {
  const rows: string[] = [];
  for (let i = 0; i < phones.length; i += 2) {
    rows.push(phones.slice(i, i + 2).join(' - '));
  }
  return rows;
};

const Invoice: React.FC<InvoiceProps> = ({ booking, vendorInfo: vendorInfoProp }) => {
  const [vendorInfo, setVendorInfo] = useState<VendorInfo>(vendorInfoProp || {});
  const [isLoadingVendor, setIsLoadingVendor] = useState(!vendorInfoProp);
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string | null>(null);
  const [locationQrCodeDataUrl, setLocationQrCodeDataUrl] = useState<string | null>(null);

  // Fetch vendor data (skip when provided for public invoice page)
  useEffect(() => {
    if (vendorInfoProp) {
      setVendorInfo(vendorInfoProp);
      setIsLoadingVendor(false);
      return;
    }

    const fetchVendorData = async () => {
      try {
        setIsLoadingVendor(true);
        const vendorData = await apiService.getVendorProfile();
        setVendorInfo({
          name: vendorData?.name,
          companyName: vendorData?.companyName,
          license: vendorData?.license || {},
          contact: vendorData?.contact || {},
          logo: vendorData?.logo
        });
      } catch (error) {
        console.error('Error fetching vendor data:', error);
        setVendorInfo({});
      } finally {
        setIsLoadingVendor(false);
      }
    };

    fetchVendorData();
  }, [vendorInfoProp]);

  const getTripTypeLabel = (type: string) => {
    const types = {
      'hajj': 'حج',
      'umrah': 'عمرة',
      'transport': 'نقل'
    };
    return types[type as keyof typeof types] || type;
  };

  const getPrimaryRouteDisplay = () => {
    if (booking.routes && booking.routes.length > 0) {
      const activeRoute = booking.routes.find(r => r.isActive) || booking.routes[0];
      return {
        from: activeRoute.from,
        to: activeRoute.to,
        departureDateValue: activeRoute.dateFrom,
        departureTime: activeRoute.timeFrom,
        returnDateValue: activeRoute.dateTo,
        returnTime: activeRoute.timeTo,
        extraRoutes: booking.routes.filter(r => r !== activeRoute),
      };
    }

    if (booking.route) {
      const depStr = String(booking.route.departureDate);
      const retStr = String(booking.route.returnDate);
      return {
        from: booking.route.from,
        to: booking.route.to,
        departureDateValue: depStr.split('T')[0],
        departureTime: depStr.split('T')[1]?.slice(0, 5) || '',
        returnDateValue: retStr.split('T')[0],
        returnTime: retStr.split('T')[1]?.slice(0, 5) || '',
        extraRoutes: [],
      };
    }

    return null;
  };

  // Calculate passenger counts by gender
  const getPassengerCounts = () => {
    let totalCount = booking.contactPerson ? 1 : 0;
    let maleCount = booking.contactPerson?.gender === 'male' ? 1 : 0;
    let femaleCount = booking.contactPerson?.gender === 'female' ? 1 : 0;

    if (booking.companions && booking.companions.length > 0) {
      totalCount += booking.companions.length;
      booking.companions.forEach(companion => {
        if (companion.gender === 'male') {
          maleCount++;
        } else if (companion.gender === 'female') {
          femaleCount++;
        }
      });
    }

    return { totalCount, maleCount, femaleCount };
  };

  const { totalCount, maleCount, femaleCount } = getPassengerCounts();
  const primaryRoute = getPrimaryRouteDisplay();

  const getRoutesForInvoice = () => {
    if (booking.routes && booking.routes.length > 0) {
      return booking.routes.map((route) => ({
        from: route.from,
        to: route.to,
        dateValue: route.dateFrom,
        time: route.timeFrom,
      }));
    }

    if (primaryRoute) {
      const routes = [{
        from: primaryRoute.from,
        to: primaryRoute.to,
        dateValue: primaryRoute.departureDateValue,
        time: primaryRoute.departureTime,
      }];

      primaryRoute.extraRoutes.forEach((route) => {
        routes.push({
          from: route.from,
          to: route.to,
          dateValue: route.dateFrom,
          time: route.timeFrom,
        });
      });

      return routes;
    }

    return [];
  };

  const invoiceRoutes = getRoutesForInvoice();
  const officeMapsUrl = buildOfficeMapsUrl(vendorInfo.contact?.address);
  // Office's own numbers; the built-in ones are only a fallback when the office has none
  const vendorPhones = [vendorInfo.contact?.phone, vendorInfo.contact?.mobile].filter(Boolean) as string[];
  const contactPhoneRows = formatPhonesInPairs(
    Array.from(new Set(vendorPhones.length ? vendorPhones : INVOICE_CONTACT_PHONES))
  );
  const companyLogoSrc = getLogoUrl(vendorInfo.logo) || INVOICE_COMPANY_LOGO;

  // Calculate VAT amount (15% VAT rate for Saudi Arabia)
  const vatRate = 0.15;
  const totalAmount = Number(booking.pricing?.totalAmount) || 0;
  const vatAmount = totalAmount * vatRate / (1 + vatRate);

  // ZATCA expects an ISO 8601 timestamp, e.g. 2026-10-19T10:40:00Z
  const invoiceDateIso = (() => {
    const d = new Date(booking.createdAt);
    const valid = Number.isNaN(d.getTime()) ? new Date() : d;
    return `${valid.toISOString().slice(0, 19)}Z`;
  })();

  // Generate ZATCA QR Code data
  const zatcaQRCodeData = vendorInfo.companyName && vendorInfo.license?.taxNumber
    ? generateZATCAQRCode(
        vendorInfo.companyName,
        vendorInfo.license.taxNumber,
        invoiceDateIso,
        totalAmount,
        vatAmount
      )
    : null;

  // Generate QR Code image when data is available
  useEffect(() => {
    if (zatcaQRCodeData) {
      QRCode.toDataURL(zatcaQRCodeData, {
        width: 80,
        margin: 1,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      })
        .then((url: string) => {
          setQrCodeDataUrl(url);
        })
        .catch((err: any) => {
          console.error('Error generating QR code:', err);
        });
    } else {
      setQrCodeDataUrl(null);
    }
  }, [zatcaQRCodeData]);

  // Generate office location QR Code (Google Maps)
  useEffect(() => {
    if (!officeMapsUrl) {
      setLocationQrCodeDataUrl(null);
      return;
    }

    QRCode.toDataURL(officeMapsUrl, {
      width: 96,
      margin: 1,
      color: {
        dark: '#000000',
        light: '#FFFFFF'
      }
    })
      .then((url: string) => {
        setLocationQrCodeDataUrl(url);
      })
      .catch((err: any) => {
        console.error('Error generating location QR code:', err);
        setLocationQrCodeDataUrl(null);
      });
  }, [officeMapsUrl]);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cairo:wght@200;300;400;500;600;700;800;900&display=swap');
        @import url('https://fonts.googleapis.com/css2?family=Noto+Sans+Arabic:wght@100;200;300;400;500;600;700;800;900&display=swap');
        @import url('https://fonts.googleapis.com/css2?family=Amiri:wght@400;700&display=swap');

        .invoice-themed {
          --invoice-navy: #0c1835;
          --invoice-navy-light: #152547;
          --invoice-gold: #c9a227;
          --invoice-gold-dark: #a0753d;
          --invoice-cream: #faf6ee;
          background: var(--invoice-navy);
          padding: 14px;
          print-color-adjust: exact;
        }

        @media screen {
          .invoice-themed {
            min-height: 1050px;
          }
        }

        .invoice-frame {
          position: relative;
          border: 2px solid var(--invoice-gold);
          border-radius: 0 0 12px 12px;
          overflow: hidden;
          print-color-adjust: exact;
        }

        @media screen {
          .invoice-frame {
            min-height: 1022px;
          }
        }

        .invoice-corner {
          position: absolute;
          width: 36px;
          height: 36px;
          z-index: 5;
          pointer-events: none;
        }

        .invoice-corner::before,
        .invoice-corner::after {
          content: '';
          position: absolute;
          background: var(--invoice-gold);
        }

        .invoice-corner-tl { top: 8px; left: 8px; }
        .invoice-corner-tl::before { top: 0; left: 0; width: 22px; height: 2px; }
        .invoice-corner-tl::after { top: 0; left: 0; width: 2px; height: 22px; }

        .invoice-corner-tr { top: 8px; right: 8px; }
        .invoice-corner-tr::before { top: 0; right: 0; width: 22px; height: 2px; }
        .invoice-corner-tr::after { top: 0; right: 0; width: 2px; height: 22px; }

        .invoice-corner-bl { bottom: 8px; left: 8px; }
        .invoice-corner-bl::before { bottom: 0; left: 0; width: 22px; height: 2px; border-radius: 0 0 0 4px; }
        .invoice-corner-bl::after { bottom: 0; left: 0; width: 2px; height: 22px; border-radius: 0 0 0 4px; }

        .invoice-corner-br { bottom: 8px; right: 8px; }
        .invoice-corner-br::before { bottom: 0; right: 0; width: 22px; height: 2px; border-radius: 0 0 4px 0; }
        .invoice-corner-br::after { bottom: 0; right: 0; width: 2px; height: 22px; border-radius: 0 0 4px 0; }

        .invoice-sheet {
          display: flex;
          flex-direction: column;
          background: var(--invoice-cream);
        }

        @media screen {
          .invoice-sheet {
            min-height: 1022px;
          }
        }

        .invoice-header-themed {
          position: relative;
          background: var(--invoice-navy);
          overflow: hidden;
          print-color-adjust: exact;
        }

        .invoice-header-bg {
          width: 100%;
          height: auto;
          display: block;
          print-color-adjust: exact;
        }

        .invoice-body-panel {
          flex: 1;
          background: linear-gradient(180deg, var(--invoice-cream) 0%, #fffdf9 100%);
          position: relative;
          border-radius: 0 0 8px 8px;
          print-color-adjust: exact;
        }

        .invoice-body-panel::before {
          content: '';
          position: absolute;
          top: -24px;
          left: 0;
          right: 0;
          height: 48px;
          background: var(--invoice-cream);
          border-top: 2px solid var(--invoice-gold);
          border-radius: 50% 50% 0 0 / 100% 100% 0 0;
          box-shadow: 0 -4px 12px rgba(12, 24, 53, 0.06);
        }

        .invoice-header-content {
          position: absolute;
          inset: 0;
          z-index: 2;

          /* ═══ يمين الهيدر: شعار رؤية 2030 (شاشة) ═══ */
          --screen-vision-logo-x: -2rem;
          --screen-vision-logo-y: 2rem;

          /* ═══ وسط الهيدر: شعار الشركة (شاشة) ═══ */
          --screen-company-logo-x: 0;
          --screen-company-logo-y: 0.5rem;

          /* ═══ يسار الهيدر: باركود + بيانات الفاتورة (شاشة) ═══ */
          --screen-qr-x: 2rem;
          --screen-qr-y: 2rem;
          --screen-meta-x: 1.5rem;
        }

        .invoice-header-vision-slot {
          transform: translate(var(--screen-vision-logo-x), var(--screen-vision-logo-y));
        }

        .invoice-header-company-logo {
          transform: translate(var(--screen-company-logo-x), var(--screen-company-logo-y));
        }

        .invoice-header-qr-slot {
          transform: translate(var(--screen-qr-x), var(--screen-qr-y));
        }

        .invoice-header-meta {
          transform: translateX(var(--screen-meta-x));
        }

        .invoice-section-frame {
          border: 1px solid var(--invoice-gold);
          background: transparent;
          print-color-adjust: exact;
        }

        .invoice-section-block,
        .invoice-keep-together {
          break-inside: avoid;
          page-break-inside: avoid;
        }

        .invoice-title-icon-box {
          width: 2.375rem;
          height: 2.375rem;
          border: 1.5px solid var(--invoice-gold);
          border-radius: 9999px;
          print-color-adjust: exact;
        }

        .invoice-title-icon {
          color: var(--invoice-gold);
        }

        .invoice-company-gold {
          color: var(--invoice-gold);
        }

        .invoice-company-title {
          font-family: 'Amiri', 'Noto Sans Arabic', serif;
        }

        .invoice-company-name {
          color: #111827;
          font-family: 'Amiri', 'Noto Sans Arabic', serif;
          font-size: 1.5rem;
          font-weight: 700;
          line-height: 1.25;
        }

        @media (min-width: 640px) {
          .invoice-company-name {
            font-size: 3rem;
          }
        }

        .invoice-company-line {
          display: inline-block;
          width: 3rem;
          height: 2px;
          background: var(--invoice-gold);
          border-radius: 1px;
          flex-shrink: 0;
          print-color-adjust: exact;
        }

        @media (min-width: 640px) {
          .invoice-company-line {
            width: 3rem;
          }
        }

        /* رابط واتساب / PDF — هيدر أصغر قليلاً + شعار رؤية أبيض */
        .invoice-pdf-source .invoice-header-content {
          --screen-vision-logo-x: 0rem; /* 1rem كانت تطلّع التراخيص برّا الإطار */
          --screen-vision-logo-y: 1.85rem; /* نزّل شعار رؤية */
          /* المسافة الإضافية تحت الشعار لنزول التراخيص (margin) */
          --screen-licenses-y: 2.55rem;
          --screen-company-logo-x: 0;
          --screen-company-logo-y: 1rem; /* نزّل شعار الحج بالوسط */
          --screen-qr-x: 1rem;
          --screen-qr-y: 1.85rem; /* نزّل QR */
          /* رقم الفاتورة + التاريخ + الرقم الضريبي */
          --screen-meta-x: 0.5rem;
          --screen-meta-y: 1rem; /* نزّل البيانات تحت QR */
          padding-top: 0.65rem !important;
          padding-bottom: 0.9rem !important;
          padding-inline: 0.9rem !important;
        }

        /* لازم visible وإلا نزول التراخيص ينقص داخل الهيدر */
        .invoice-pdf-source .invoice-header-themed,
        .invoice-pdf-source .invoice-header-content {
          overflow: visible !important;
        }

        .invoice-pdf-source .invoice-header-row {
          gap: 0.45rem !important;
        }

        .invoice-pdf-source .invoice-header-vision-slot {
          margin-bottom: 0 !important;
        }

        .invoice-pdf-source .invoice-header-qr-slot {
          margin-bottom: 0.65rem !important;
        }

        .invoice-pdf-source .invoice-header-ksa-logo {
          width: 4rem !important;
          height: 4rem !important;
          filter: brightness(0) invert(1) !important;
          -webkit-filter: brightness(0) invert(1) !important;
        }

        .invoice-pdf-source .invoice-header-company-logo {
          width: 6.25rem !important;
          height: 6.5rem !important;
          margin-bottom: 0.35rem !important;
        }

        .invoice-pdf-source .invoice-header-qr-box {
          width: 4.25rem !important;
          height: 4.25rem !important;
        }

        .invoice-pdf-source .invoice-company-name {
          font-size: 1.55rem !important;
          line-height: 1.2 !important;
        }

        .invoice-pdf-source .invoice-company-gold {
          font-size: 1.05rem !important;
        }

        .invoice-pdf-source .invoice-company-line {
          width: 2rem !important;
        }

        .invoice-pdf-source .invoice-header-licenses,
        .invoice-pdf-source .invoice-header-meta {
          gap: 0.4rem !important;
          row-gap: 0.4rem !important;
        }

        .invoice-pdf-source .invoice-header-licenses.space-y-4 > :not([hidden]) ~ :not([hidden]),
        .invoice-pdf-source .invoice-header-meta.space-y-4 > :not([hidden]) ~ :not([hidden]) {
          margin-top: 0.4rem !important;
        }

        .invoice-pdf-source .invoice-license-label {
          font-size: 0.78rem !important;
          line-height: 1.25 !important;
        }

        .invoice-pdf-source .invoice-license-value {
          font-size: 0.72rem !important;
          line-height: 1.25 !important;
        }

        /* يمين/يسار التراخيص مع الشعار، والنزول عبر --screen-licenses-y */
        .invoice-pdf-source .invoice-header-licenses,
        .invoice-pdf-source .invoice-header-licenses.-translate-x-6 {
          transform: translateX(var(--screen-vision-logo-x)) !important;
          margin-top: var(--screen-licenses-y) !important;
        }

        /* رقم الفاتورة / التاريخ / الرقم الضريبي مع QR */
        .invoice-pdf-source .invoice-header-meta {
          transform: translate(
            var(--screen-meta-x),
            var(--screen-meta-y)
          ) !important;
        }

        .invoice-customer-fields .invoice-stacked-field-sep {
          border-inline-start: 1px solid #d1d5db;
          print-color-adjust: exact;
        }

        .invoice-route-block {
          overflow: visible;
          print-color-adjust: exact;
        }

        .invoice-route-index {
          display: inline-flex !important;
          align-items: center !important;
          justify-content: center !important;
          line-height: 1 !important;
          padding: 0 !important;
          /* الدائرة ثابتة — لا تحركها */
          transform: none !important;
        }

        /* حرّكي الرقم فقط داخل الدائرة */
        .invoice-route-index-num,
        .invoice-pdf-source .invoice-route-index-num {
          display: inline-block;
          line-height: 1;
          transform: translateY(-0.25em);
        }

        .invoice-route-row {
          display: grid;
          grid-template-columns: 1.75rem auto auto 2rem auto auto minmax(0, 1fr);
          grid-template-rows: auto;
          align-items: center;
          column-gap: 0.35rem;
          overflow: visible;
        }

        .invoice-route-meta {
          grid-column: 7;
          grid-row: 1;
          justify-self: stretch;
          align-items: center;
        }

        /* أسماء المدن: كاملة وواضحة، بدون قص */
        .invoice-route-city {
          display: inline-block;
          text-align: start;
          white-space: nowrap;
          overflow: visible;
          text-overflow: clip;
          max-width: none;
          line-height: 1.35;
          padding-block: 0.1rem;
          align-self: center;
        }

        .invoice-route-row .invoice-route-arrow {
          justify-self: center;
          align-self: center;
          print-color-adjust: exact;
        }

        /* PDF / واتساب: ارفع أسماء المدن (حائل / مكة…) فوق شوي بدون قص */
        .invoice-pdf-source .invoice-container {
          overflow: visible !important;
        }

        .invoice-pdf-source .invoice-route-block {
          overflow: visible !important;
          padding-top: 0.65rem !important;
          padding-bottom: 0.65rem !important;
        }

        .invoice-pdf-source .invoice-route-row {
          --screen-route-content-y: -0.3rem;
          overflow: visible !important;
          align-items: center !important;
        }

        /* ارفع محتوى المسار (من/إلى/المدن/المغادرة) فوق — الدائرة تبقى */
        .invoice-pdf-source .invoice-route-row > :not(.invoice-route-index):not(.invoice-route-arrow) {
          position: relative;
          top: var(--screen-route-content-y);
        }

        .invoice-pdf-source .invoice-route-city {
          overflow: visible !important;
          text-overflow: clip !important;
          max-width: none !important;
          line-height: 1.4 !important;
          font-size: 0.95rem !important;
          padding-block: 0.12rem !important;
        }

        .invoice-pdf-source .invoice-route-meta,
        .invoice-pdf-source .invoice-route-meta .inline-flex {
          align-items: center !important;
        }

        .invoice-pdf-source .invoice-route-arrow {
          --screen-route-arrow-y: 0.08rem;
          transform: translateY(var(--screen-route-arrow-y));
        }

        /* html2canvas يرسم الحروف حرفاً حرفاً لو letter-spacing ≠ 0 فيتكسّر الخط العربي في PDF */
        .invoice-container h1,
        .invoice-container h2,
        .invoice-container h3 {
          letter-spacing: normal;
        }

        /* PDF: html2canvas ينزّل النص عن مكانه في المتصفح، فننزّل أيقونة الحقل بنفس المقدار */
        .invoice-pdf-source .invoice-field-icon {
          --pdf-field-icon-y: 0.14em;
          transform: translateY(var(--pdf-field-icon-y));
        }


        .invoice-currency {
          direction: ltr;
          unicode-bidi: isolate;
        }

        .invoice-footer {
          border-top: 1px solid var(--invoice-gold);
        }

        .invoice-footer-grid {
          display: grid;
          grid-template-columns: 1fr 1px 1fr 1px 1fr;
          align-items: stretch;
        }

        .invoice-footer-divider {
          background: var(--invoice-gold);
          align-self: stretch;
          width: 1px;
          min-height: 100%;
        }

        .invoice-footer-cell {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          padding-inline: 0.75rem;
          min-width: 0;
        }

      `}</style>
      <div
        className="invoice-container invoice-themed relative overflow-hidden text-black font-arabic"
        style={{ direction: 'rtl', printColorAdjust: 'exact' }}
      >
        <div className="invoice-frame">
          <span className="invoice-corner invoice-corner-tl" aria-hidden />
          <span className="invoice-corner invoice-corner-tr" aria-hidden />
          <span className="invoice-corner invoice-corner-bl" aria-hidden />
          <span className="invoice-corner invoice-corner-br" aria-hidden />

          <div className="invoice-sheet">
        {/* Header — banner image with content overlay */}
        <div className="invoice-header invoice-header-themed relative shrink-0">
          <img
            src={INVOICE_HEADER_BG}
            alt=""
            aria-hidden
            className="invoice-header-bg w-full h-auto"
          />
          <div className="invoice-header-content px-4 sm:px-6 pt-4 pb-6">
            <div className="invoice-header-row flex justify-between items-start gap-3 sm:gap-4 w-full">
              {/* Right — Vision logo and licenses */}
              <div className="flex-1 flex justify-start min-w-0">
                <div className="flex flex-col items-center">
                  <div className="invoice-header-vision-slot mb-12">
                    <img
                      src="/ksa.png"
                      alt="رؤية المملكة العربية السعودية 2030"
                      className="invoice-header-ksa-logo w-28 h-28 sm:w-32 sm:h-32 object-contain brightness-0 invert"
                    />
                  </div>

                  {!isLoadingVendor && (
                    <div className="invoice-header-licenses space-y-4 text-center drop-shadow-sm -translate-x-6">
                    {vendorInfo.license?.commercialRegister && (
                      <div className="flex flex-col items-center gap-0.5">
                        <span className={INVOICE_LICENSE_LABEL_CLASS}>السجل التجاري:</span>
                        <span className={INVOICE_LICENSE_VALUE_CLASS}>{vendorInfo.license.commercialRegister}</span>
                      </div>
                    )}
                    {vendorInfo.license?.transportationLicense && (
                      <div className="flex flex-col items-center gap-0.5">
                        <span className={INVOICE_LICENSE_LABEL_CLASS}>ترخيص النقل:</span>
                        <span className={INVOICE_LICENSE_VALUE_CLASS}>{vendorInfo.license.transportationLicense}</span>
                      </div>
                    )}
                    {vendorInfo.license?.tourismLicense && (
                      <div className="flex flex-col items-center gap-0.5">
                        <span className={INVOICE_LICENSE_LABEL_CLASS}>ترخيص السياحة:</span>
                        <span className={INVOICE_LICENSE_VALUE_CLASS}>{vendorInfo.license.tourismLicense}</span>
                      </div>
                    )}
                  </div>
                )}

                {isLoadingVendor && (
                  <div className="text-xs sm:text-sm text-center text-white/90">جاري تحميل بيانات الترخيص...</div>
                )}

                {!isLoadingVendor && !vendorInfo.license?.commercialRegister &&
                 !vendorInfo.license?.transportationLicense && !vendorInfo.license?.tourismLicense && (
                  <div className="text-xs sm:text-sm text-center text-white/90">لا توجد بيانات ترخيص متاحة</div>
                )}
                </div>
              </div>

            {/* Center — logo and company name */}
            <div className="text-center flex-1 min-w-0 px-1">
              <div className="invoice-header-company-logo w-42 h-44 sm:w-40 sm:h-38 bg-transparent rounded mx-auto mb-2 flex items-center justify-center overflow-hidden">
                <img
                  src={companyLogoSrc}
                  crossOrigin={companyLogoSrc === INVOICE_COMPANY_LOGO ? undefined : 'anonymous'}
                  alt="شعار المكتب"
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="drop-shadow-sm">
                {!isLoadingVendor && vendorInfo.companyName ? (
                  <InvoiceCompanyName name={vendorInfo.companyName} />
                ) : (
                  <span className="text-lg sm:text-2xl font-bold text-gray-900">
                    {isLoadingVendor ? 'جاري تحميل...' : 'اسم الشركة غير متاح'}
                  </span>
                )}
              </div>
            </div>

            {/* Left — ZATCA QR and invoice meta (mirror of Vision/licenses column) */}
            <div className="flex-1 flex justify-end min-w-0">
              <div className="flex flex-col items-center">
                <div className="invoice-header-qr-slot mb-12 relative">
                  {qrCodeDataUrl ? (
                    <div className="invoice-header-qr-box w-20 h-20 sm:w-24 sm:h-24 bg-white border border-gray-300 rounded flex items-center justify-center overflow-hidden relative">
                      <img
                        src={qrCodeDataUrl}
                        alt="رمز التحقق الضريبي ZATCA"
                        className="w-full h-full object-contain"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                          const fallback = e.currentTarget.parentElement?.querySelector('.qr-fallback');
                          if (fallback) {
                            (fallback as HTMLElement).style.display = 'flex';
                          }
                        }}
                      />
                      <div
                        className="absolute opacity-0 pointer-events-none"
                        data-qr-content={zatcaQRCodeData}
                        title="رمز التحقق الضريبي ZATCA"
                      >
                        {zatcaQRCodeData}
                      </div>
                      <div className="qr-fallback text-gray-900 text-xs absolute inset-0 flex items-center justify-center" style={{ display: 'none' }}>
                        رمز ZATCA
                      </div>
                    </div>
                  ) : zatcaQRCodeData ? (
                    <div className="invoice-header-qr-box w-20 h-20 sm:w-24 sm:h-24 bg-gray-50 border border-gray-300 rounded flex items-center justify-center">
                      <div className="text-gray-900 text-sm">جاري التحميل...</div>
                    </div>
                  ) : (
                    <div className="invoice-header-qr-box w-20 h-20 sm:w-24 sm:h-24 bg-gray-100 border border-gray-300 rounded flex items-center justify-center">
                      <div className="text-gray-500 text-sm">لا يوجد رمز</div>
                    </div>
                  )}
                </div>

                <div className="invoice-header-meta space-y-4 text-center drop-shadow-sm">
                  <div className="flex flex-col items-center gap-0.5">
                    <span className={INVOICE_LICENSE_LABEL_CLASS}>رقم الفاتورة:</span>
                    <span className={INVOICE_LICENSE_VALUE_CLASS}>{booking.bookingNumber}</span>
                  </div>
                  <div className="flex flex-col items-center gap-0.5">
                    <span className={INVOICE_LICENSE_LABEL_CLASS}>تاريخ الفاتورة:</span>
                    <DateDisplay
                      value={booking.createdAt}
                      showTime
                      className="[&_span]:!text-white items-center"
                      gregorianClassName={`${INVOICE_LICENSE_VALUE_CLASS} leading-tight`}
                      hijriClassName="text-xs text-white/90 leading-tight mt-0.5"
                    />
                  </div>
                  {!isLoadingVendor && vendorInfo.license?.taxNumber && (
                    <div className="flex flex-col items-center gap-0.5">
                      <span className={INVOICE_LICENSE_LABEL_CLASS}>الرقم الضريبي:</span>
                      <span className={INVOICE_LICENSE_VALUE_CLASS}>{vendorInfo.license.taxNumber}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        </div>

        <div className="invoice-body-panel">
        {/* Body sections — each in one row, separated by divider */}
        <div className="invoice-body relative z-10 bg-transparent px-4 sm:px-6 pt-4 pb-6 print:p-0">

        <InvoiceSection title="بيانات العميل" icon={UserIcon}>
          <div className="invoice-keep-together invoice-customer-fields flex flex-row items-stretch">
            <InvoiceStackedField label="الاسم" icon={UserIcon}>{booking.contactPerson?.name || '—'}</InvoiceStackedField>
            <InvoiceStackedField label="الجنسية" icon={GlobeAltIcon} separated>{booking.contactPerson?.nationality || '—'}</InvoiceStackedField>
            <InvoiceStackedField label="الهوية" icon={IdentificationIcon} separated>{booking.contactPerson?.idNumber || '—'}</InvoiceStackedField>
            <InvoiceStackedField label="الجوال" icon={DevicePhoneMobileIcon} separated>{booking.contactPerson?.phone || '—'}</InvoiceStackedField>
          </div>
        </InvoiceSection>

        <InvoiceSection title="تفاصيل الرحلة" icon={TruckIcon}>
          <div className="text-gray-800">
            <div className="invoice-keep-together flex flex-wrap items-center gap-x-8 gap-y-2 text-base">
              <span className="inline-flex flex-wrap items-center gap-x-3 gap-y-1">
                <span className="font-bold text-black text-lg">النوع:</span>
                <span className="font-bold text-[#1e3a5f] text-lg">{getTripTypeLabel(booking.tripType)}</span>
              </span>
              <span className="inline-flex flex-wrap items-center gap-x-3 gap-y-1">
                <span className="font-bold text-black text-lg">عدد الأشخاص:</span>
                <span className="font-bold text-gray-900 text-lg">{totalCount}</span>
              </span>
            </div>

            {invoiceRoutes.length > 0 ? (
              <>
                <div className={INVOICE_INNER_DIVIDER_CLASS} />
                <p className="invoice-keep-together font-bold text-black text-lg mb-3">المسارات:</p>
                <div className="space-y-3">
                  {invoiceRoutes.map((route, idx) => (
                    <InvoiceRouteBlock
                      key={`${route.from}-${route.to}-${idx}`}
                      index={idx + 1}
                      from={route.from}
                      to={route.to}
                      dateValue={route.dateValue}
                      time={route.time}
                    />
                  ))}
                </div>
              </>
            ) : (
              <>
                <div className={INVOICE_INNER_DIVIDER_CLASS} />
                <p className="invoice-keep-together text-gray-500 text-base">لا توجد مسارات مسجلة لهذا الحجز</p>
              </>
            )}

            {(booking.assignedBus || booking.assignedTrip || booking.assignedAccommodation) && (
              <div className="invoice-keep-together">
                <div className={INVOICE_INNER_DIVIDER_CLASS} />
                <p className="font-bold text-black text-base mb-2">معلومات إضافية</p>
                <div className="invoice-customer-fields flex flex-row items-stretch rounded-lg border border-[#e8dcc0] bg-transparent overflow-hidden">
                  {booking.assignedBus && (
                    <InvoiceStackedField label="الحافلة" separated={false}>{booking.assignedBus.busNumber}</InvoiceStackedField>
                  )}
                  {booking.assignedTrip && (
                    <InvoiceStackedField label="الرحلة" separated={!!booking.assignedBus}>{booking.assignedTrip.tripNumber}</InvoiceStackedField>
                  )}
                  {booking.assignedAccommodation && (
                    <InvoiceStackedField label="السكن" separated={!!(booking.assignedBus || booking.assignedTrip)}>{booking.assignedAccommodation.name}</InvoiceStackedField>
                  )}
                </div>
              </div>
            )}

            <div className="invoice-keep-together">
              <InvoicePassengerCounts maleCount={maleCount} femaleCount={femaleCount} />
            </div>
          </div>
        </InvoiceSection>

        {booking.companions && booking.companions.length > 0 && (
          <InvoiceSection title={`قائمة المرافقين (${booking.companions.length})`} icon={UsersIcon}>
            <div className="space-y-3">
              {booking.companions.map((companion, index) => (
                <div key={index} className="invoice-keep-together">
                  {index > 0 && <div className={INVOICE_INNER_DIVIDER_CLASS} />}
                  <div className="invoice-customer-fields flex flex-row items-stretch">
                    <InvoiceStackedField label="الاسم" icon={UserIcon}>{companion.name}</InvoiceStackedField>
                    <InvoiceStackedField label="الجنسية" icon={GlobeAltIcon} separated>{companion.nationality}</InvoiceStackedField>
                    <InvoiceStackedField label="الهوية" icon={IdentificationIcon} separated>{companion.idNumber}</InvoiceStackedField>
                    <InvoiceStackedField label="الجنس" separated>{companion.gender === 'male' ? 'ذكر' : 'أنثى'}</InvoiceStackedField>
                  </div>
                </div>
              ))}
            </div>
          </InvoiceSection>
        )}

        {booking.assignedTrip &&
          (booking.contactPerson?.boardingToken ||
            (booking.companions || []).some((c) => c.boardingToken)) && (
          <InvoiceSection title="بطاقات بوابة الصعود" icon={QrCodeIcon}>
            <div className="flex flex-wrap gap-6 justify-center py-2">
              {booking.contactPerson?.boardingToken && (
                <div className="invoice-keep-together">
                  <BoardingQrCode
                    token={booking.contactPerson.boardingToken}
                    size={120}
                    showToken={false}
                    label={`${booking.contactPerson.name || ''}${
                      booking.contactPerson.seatNumber
                        ? ` — مقعد ${booking.contactPerson.seatNumber}`
                        : ''
                    }`}
                  />
                </div>
              )}
              {(booking.companions || [])
                .filter((c) => c.boardingToken)
                .map((companion, idx) => (
                  <div key={companion.boardingToken || idx} className="invoice-keep-together">
                    <BoardingQrCode
                      token={companion.boardingToken!}
                      size={120}
                      showToken={false}
                      label={`${companion.name}${
                        companion.seatNumber ? ` — مقعد ${companion.seatNumber}` : ''
                      }`}
                    />
                  </div>
                ))}
            </div>
          </InvoiceSection>
        )}

        {booking.specialRequests && (
          <InvoiceSection title="طلبات خاصة" icon={ClipboardDocumentListIcon}>
            <p className="text-base text-gray-800 leading-relaxed whitespace-pre-wrap">{booking.specialRequests}</p>
          </InvoiceSection>
        )}

        <InvoiceSection title="التفاصيل المالية" icon={BanknotesIcon}>
          <div className="invoice-keep-together">
            {booking.pricing.discount > 0 && (
              <>
                <InvoiceAmountRow label="الخصم">
                  <InvoiceCurrency amount={booking.pricing.discount} prefix="-" className="text-red-600" />
                </InvoiceAmountRow>
                <div className={INVOICE_INNER_DIVIDER_CLASS} />
              </>
            )}
            <InvoiceAmountRow label="المبلغ الإجمالي">
              <InvoiceCurrency amount={booking.pricing.totalAmount} />
            </InvoiceAmountRow>
            <div className={INVOICE_INNER_DIVIDER_CLASS} />
            <InvoiceAmountRow label="المبلغ المدفوع">
              <InvoiceCurrency amount={booking.pricing.paidAmount} />
            </InvoiceAmountRow>
            {booking.pricing.remainingAmount > 0 && (
              <>
                <div className={INVOICE_INNER_DIVIDER_CLASS} />
                <InvoiceAmountRow label="المبلغ المتبقي" highlight>
                  <InvoiceCurrency amount={booking.pricing.remainingAmount} className="text-orange-600" />
                </InvoiceAmountRow>
              </>
            )}
          </div>
        </InvoiceSection>

        {/* Footer — contact | address | QR */}
        <div className="invoice-footer pt-4">
          <div className="invoice-footer-grid">
            <div className="invoice-footer-cell">
              <div className="flex items-center justify-center gap-1.5 mb-1">
                <InvoiceTitleIcon icon={PhoneIcon} />
                <span className="font-bold text-base text-black">للتواصل</span>
              </div>
              <div className="text-base text-gray-700 space-y-0.5" dir="ltr">
                {isLoadingVendor && contactPhoneRows.length === 0 ? (
                  <div className="text-gray-500">جاري التحميل...</div>
                ) : (
                  contactPhoneRows.map((row, index) => (
                    <div key={index}>{row}</div>
                  ))
                )}
              </div>
            </div>

            <div className="invoice-footer-divider" aria-hidden />

            <div className="invoice-footer-cell">
              <div className="flex items-center justify-center gap-1.5 mb-1">
                <InvoiceTitleIcon icon={MapPinIcon} />
                <span className="font-bold text-base text-black">الموقع</span>
              </div>
              <div className="text-base text-gray-700 leading-relaxed">
                {!isLoadingVendor && vendorInfo.contact?.address ? (
                  <span>
                    {[
                      vendorInfo.contact.address.street,
                      vendorInfo.contact.address.city,
                      vendorInfo.contact.address.state,
                    ]
                      .filter(Boolean)
                      .join(' - ') || 'لا توجد معلومات عنوان متاحة'}
                  </span>
                ) : isLoadingVendor ? (
                  <span>جاري تحميل العنوان...</span>
                ) : (
                  <span>لا توجد معلومات عنوان متاحة</span>
                )}
              </div>
            </div>

            <div className="invoice-footer-divider" aria-hidden />

            <div className="invoice-footer-cell justify-center">
              {locationQrCodeDataUrl ? (
                <div className="invoice-footer-qr w-24 h-24 shrink-0 bg-white border border-gray-300 rounded flex items-center justify-center">
                  <img
                    src={locationQrCodeDataUrl}
                    alt="رمز موقع المكتب على الخريطة"
                    className="w-full h-full object-contain"
                  />
                </div>
              ) : (
                <div className="w-24 h-24 shrink-0" aria-hidden />
              )}
            </div>
          </div>
        </div>
          </div>
        </div>
        </div>
      </div>
      </div>
    </>
  );
};

export default Invoice; 